/**
 * Text Preview Overlay Component
 * 
 * Displays custom text input as a live preview overlay on product images.
 * Features auto-fit text scaling to ensure text always fits within the defined bounding box.
 */

class TextPreviewOverlay extends HTMLElement {
  constructor() {
    super();
    
    // Configuration from data attributes
    this.config = {
      maxFontSize: 24,
      minFontSize: 8,
      inputSelector1: null,
      inputSelector2: null,
      showPreview1: true,
      showPreview2: true,
    };
    
    // Bound methods for event listeners
    this.handleInput = this.handleInput.bind(this);
    this.handleResize = this.debounce(this.handleResize.bind(this), 100);
    this.handleVariantChange = this.handleVariantChange.bind(this);
  }

  connectedCallback() {
    this.parseConfig();
    
    // Store the initial HTML content as a template for injection
    // We'll clone this into each matching slide
    this.templateHTML = this.innerHTML;
    this.innerHTML = ''; // Clear content to avoid duplicates/ghosts
    
    // Delay caching and binding to ensure inputs are in DOM
    requestAnimationFrame(() => {
      // Inject overlays into slides
      this.injectOverlays();
      
      this.cacheElements();
      this.bindEvents();
      this.initializePreview();
      
      // Debug logging
      console.log('[TextPreviewOverlay] Initialized (Multi-slide mode)', {
        input1Found: !!this.input1,
        input2Found: !!this.input2,
        config: this.config
      });
      
      // Show visual guide in theme editor
      if (Shopify?.designMode) {
        this.showEditorGuide();
      }
    });
  }

  /**
   * Inject overlay clones into matching slides
   */
  injectOverlays() {
    // Find the section and media gallery
    const section = this.closest('.shopify-section') || document.querySelector(`[data-section-id="${this.config.sectionId}"]`) || document.querySelector('.shopify-section');
    if (!section) return;
    
    const mediaGallery = section.querySelector('media-gallery');
    let targets = [];
    
    if (mediaGallery) {
      // Get all slides
      const slides = mediaGallery.querySelectorAll('slideshow-slide');
      if (slides.length > 0) {
        targets = Array.from(slides);
      } else {
        // Maybe single image in gallery
        const media = mediaGallery.querySelector('.product-media-container');
        if (media) targets = [media];
      }
    } else {
      // Fallback for non-gallery setup
      const media = section.querySelector('.product-media-container');
      if (media) targets = [media];
    }
    
    console.log('[TextPreviewOverlay] Found targets:', targets.length);
    
    // Iterate targets and inject if matches criteria
    targets.forEach((target, index) => {
      // Check alt text
      const img = target.querySelector('img');
      const alt = img?.getAttribute('alt') || '';
      
      let shouldInject = true;
      if (this.config.filterByAlt) {
        shouldInject = alt && alt.toLowerCase().includes(this.config.altKeyword);
      } else if (!this.config.showOnAllSlides) {
        shouldInject = index === 0;
      }
      
      if (shouldInject) {
        // Create container for overlay in this slide
        const container = document.createElement('div');
        container.className = 'text-preview-overlay-container';
        container.innerHTML = this.templateHTML;
        container.style.pointerEvents = 'none'; // Ensure no blocking
        
        // Ensure target is relative
        const style = getComputedStyle(target);
        if (style.position === 'static') {
          target.style.position = 'relative';
        }
        
        target.appendChild(container);
        console.log(`[TextPreviewOverlay] Injected into slide ${index} (alt: ${alt})`);
      }
    });
  }

  /**
   * Move the overlay element into the media gallery container
   * DEPRECATED: Replaced by injectOverlays
   */
  moveToMediaContainer() {
    // No-op
  }

  disconnectedCallback() {
    this.unbindEvents();
  }

  /**
   * Parse configuration from data attributes
   */
  parseConfig() {
    this.config = {
      maxFontSize: parseFloat(this.dataset.maxFontSize) || 24,
      minFontSize: parseFloat(this.dataset.minFontSize) || 8,
      inputSelector1: this.dataset.inputSelector1 || null,
      inputSelector2: this.dataset.inputSelector2 || null,
      showPreview1: this.dataset.showPreview1 !== 'false',
      showPreview2: this.dataset.showPreview2 !== 'false',
      sectionId: this.dataset.sectionId || '',
      blockId: this.dataset.blockId || '',
      filterByAlt: this.dataset.filterByAlt === 'true',
      altKeyword: (this.dataset.altKeyword || 'text_customisable').toLowerCase(),
      showOnAllSlides: this.dataset.showOnAllSlides === 'true',
      // New settings for placeholder and persistence
      showPlaceholder: this.dataset.showPlaceholder !== 'false',
      persistText: this.dataset.persistText !== 'false',
      placeholder1: this.dataset.placeholder1 || '',
      placeholder2: this.dataset.placeholder2 || '',
      productHandle: this.dataset.productHandle || '',
    };
    
    console.log('[TextPreviewOverlay] parseConfig placeholder settings:', {
      showPlaceholder: this.config.showPlaceholder,
      placeholder1: this.config.placeholder1,
      placeholder2: this.config.placeholder2,
      dataAttr1: this.dataset.placeholder1,
      dataAttr2: this.dataset.placeholder2
    });
    
    // Store the current text values so they persist across slide changes
    this.storedText1 = '';
    this.storedText2 = '';
  }

  /**
   * Get the localStorage key for persisting text
   * @param {number} inputNum - 1 or 2
   * @returns {string}
   */
  getStorageKey(inputNum) {
    const productHandle = this.config.productHandle || 'default';
    const blockId = this.config.blockId || 'default';
    return `custom_text_${productHandle}_${blockId}_${inputNum}`;
  }

  /**
   * Save text to localStorage
   * @param {number} inputNum - 1 or 2
   * @param {string} text - The text to save
   */
  saveTextToStorage(inputNum, text) {
    if (!this.config.persistText) return;
    
    try {
      const key = this.getStorageKey(inputNum);
      if (text) {
        localStorage.setItem(key, text);
      } else {
        localStorage.removeItem(key);
      }
    } catch (e) {
      console.warn('[TextPreviewOverlay] Could not save to localStorage:', e);
    }
  }

  /**
   * Load text from localStorage
   * @param {number} inputNum - 1 or 2
   * @returns {string}
   */
  loadTextFromStorage(inputNum) {
    if (!this.config.persistText) return '';
    
    try {
      const key = this.getStorageKey(inputNum);
      return localStorage.getItem(key) || '';
    } catch (e) {
      console.warn('[TextPreviewOverlay] Could not load from localStorage:', e);
      return '';
    }
  }

  /**
   * Cache DOM element references
   */
  cacheElements() {
    this.textContainer1 = this.querySelector('[data-preview-text="1"]');
    this.textContainer2 = this.querySelector('[data-preview-text="2"]');
    this.boundingBox1 = this.querySelector('[data-preview-box="1"]');
    this.boundingBox2 = this.querySelector('[data-preview-box="2"]');
    
    // Find the input fields - try multiple selectors for robustness
    const blockId = this.config.blockId;
    
    // Try the configured selector first, then fallback to class-based selector
    if (this.config.inputSelector1) {
      this.input1 = document.querySelector(this.config.inputSelector1);
    }
    if (!this.input1) {
      // Fallback: find by class
      this.input1 = document.querySelector('.custom-text-input__field-1');
    }
    if (!this.input1 && blockId) {
      // Fallback: find by ID pattern
      this.input1 = document.getElementById(`custom-text-1-${blockId}`);
    }
    
    if (this.config.inputSelector2) {
      this.input2 = document.querySelector(this.config.inputSelector2);
    }
    if (!this.input2) {
      // Fallback: find by class
      this.input2 = document.querySelector('.custom-text-input__field-2');
    }
    if (!this.input2 && blockId) {
      // Fallback: find by ID pattern
      this.input2 = document.getElementById(`custom-text-2-${blockId}`);
    }
    
    // If placeholder not set in config, get it from the input field's placeholder attribute
    if (!this.config.placeholder1 && this.input1) {
      this.config.placeholder1 = this.input1.getAttribute('placeholder') || '';
    }
    if (!this.config.placeholder2 && this.input2) {
      this.config.placeholder2 = this.input2.getAttribute('placeholder') || '';
    }
    
    console.log('[TextPreviewOverlay] cacheElements', {
      selector1: this.config.inputSelector1,
      selector2: this.config.inputSelector2,
      input1: this.input1,
      input2: this.input2,
      blockId: blockId,
      placeholder1: this.config.placeholder1,
      placeholder2: this.config.placeholder2
    });
  }

  /**
   * Bind event listeners
   */
  bindEvents() {
    // Listen for input changes
    if (this.input1) {
      this.input1.addEventListener('input', this.handleInput);
      this.input1.addEventListener('change', this.handleInput);
    }
    if (this.input2) {
      this.input2.addEventListener('input', this.handleInput);
      this.input2.addEventListener('change', this.handleInput);
    }

    // Listen for window resize
    window.addEventListener('resize', this.handleResize);

    // Listen for variant changes (inputs might be re-rendered)
    document.addEventListener('variant:morphed', this.handleVariantChange);
    document.addEventListener('variant:update', this.handleVariantChange);

    // Listen for slideshow changes to update overlay visibility
    // Bind the handler to preserve 'this' context
    this.boundSlideChangeHandler = (e) => this.handleSlideChange(e);
    
    // Find the media gallery and listen for slideshow events
    const section = this.closest('.shopify-section');
    const mediaGallery = section?.querySelector('media-gallery');
    
    if (mediaGallery) {
      // Listen on the media gallery for slideshow:change events
      mediaGallery.addEventListener('slideshow:change', this.boundSlideChangeHandler);
      mediaGallery.addEventListener('slideshow:select', this.boundSlideChangeHandler);
      
      // Also listen for click events on thumbnails
      mediaGallery.addEventListener('click', (e) => {
        const thumbnail = e.target.closest('[data-slide-index], .slideshow-controls__thumbnail');
        if (thumbnail) {
          // Small delay to let the slideshow update
          setTimeout(() => {
            const activeSlide = mediaGallery.querySelector('slideshow-slide[aria-selected="true"], slideshow-slide.is-active');
            if (activeSlide) {
              const slides = mediaGallery.querySelectorAll('slideshow-slide');
              const index = Array.from(slides).indexOf(activeSlide);
              if (index !== -1) {
                this.handleSlideChange({ detail: { index } });
              }
            }
          }, 100);
        }
      });
    }
  }

  /**
   * Unbind event listeners
   */
  unbindEvents() {
    if (this.input1) {
      this.input1.removeEventListener('input', this.handleInput);
      this.input1.removeEventListener('change', this.handleInput);
    }
    if (this.input2) {
      this.input2.removeEventListener('input', this.handleInput);
      this.input2.removeEventListener('change', this.handleInput);
    }
    window.removeEventListener('resize', this.handleResize);
    document.removeEventListener('variant:morphed', this.handleVariantChange);
    document.removeEventListener('variant:update', this.handleVariantChange);
  }

  /**
   * Initialize the preview with any existing input values
   */
  initializePreview() {
    // Initialize current slide index
    this.currentSlideIndex = 0;
    
    // Try to load saved text from localStorage first
    const savedText1 = this.loadTextFromStorage(1);
    const savedText2 = this.loadTextFromStorage(2);
    
    // If we have saved text, populate the input fields
    if (savedText1 && this.input1) {
      this.input1.value = savedText1;
    }
    if (savedText2 && this.input2) {
      this.input2.value = savedText2;
    }
    
    // Store initial values (from input or localStorage)
    if (this.input1) {
      this.storedText1 = this.input1.value || '';
    }
    if (this.input2) {
      this.storedText2 = this.input2.value || '';
    }
    
    // In theme editor, always show the overlay (for positioning)
    if (Shopify?.designMode) {
      this.style.opacity = '1';
      this.style.pointerEvents = 'none';
    }
    
    // Update preview text - use actual text or placeholder
    this.updatePreviewWithPlaceholder(1);
    this.updatePreviewWithPlaceholder(2);
  }

  /**
   * Update preview text, falling back to placeholder if empty and setting enabled
   * @param {number} inputNum - 1 or 2
   */
  updatePreviewWithPlaceholder(inputNum) {
    const input = inputNum === 1 ? this.input1 : this.input2;
    const storedText = inputNum === 1 ? this.storedText1 : this.storedText2;
    const placeholder = inputNum === 1 ? this.config.placeholder1 : this.config.placeholder2;
    
    // Get the actual text value
    const actualText = input?.value || storedText || '';
    
    // If there's actual text, show it
    if (actualText) {
      this.updatePreviewText(inputNum, actualText, false);
    } else if (this.config.showPlaceholder && placeholder) {
      // Show placeholder text with special styling
      this.updatePreviewText(inputNum, placeholder, true);
    } else {
      // No text and no placeholder - hide the preview
      this.updatePreviewText(inputNum, '', false);
    }
  }

  /**
   * Handle input changes
   * @param {Event} event 
   */
  handleInput(event) {
    const input = event.target;
    if (!input) return;
    
    // Determine which input this is (1 or 2)
    let inputNum = 1;
    const inputId = input.id || '';
    const inputClass = input.className || '';
    
    if (inputId.includes('custom-text-2') || inputClass.includes('field-2')) {
      inputNum = 2;
    } else if (input === this.input2) {
      inputNum = 2;
    }
    
    const value = input.value || '';
    
    console.log('[TextPreviewOverlay] Input changed:', { inputNum, value, inputId });
    
    // Store the text value for persistence across slide changes
    if (inputNum === 1) {
      this.storedText1 = value;
    } else {
      this.storedText2 = value;
    }
    
    // Save to localStorage for persistence across page refreshes
    this.saveTextToStorage(inputNum, value);
    
    // Update preview - will show placeholder if value is empty
    this.updatePreviewWithPlaceholder(inputNum);
  }

  /**
   * Handle window resize
   */
  handleResize() {
    if (this.textContainer1) this.fitTextToBox(1);
    if (this.textContainer2) this.fitTextToBox(2);
  }

  /**
   * Handle variant changes - re-bind to new inputs if they were re-rendered
   */
  handleVariantChange() {
    // Small delay to ensure DOM is updated
    setTimeout(() => {
      // Save current stored text values before re-caching
      const savedText1 = this.storedText1;
      const savedText2 = this.storedText2;
      
      // Re-inject overlays as the gallery DOM might have been replaced
      this.injectOverlays();
      
      // Re-cache elements (inputs may have been re-rendered)
      this.cacheElements();
      this.bindInputEvents();
      
      // Restore stored text values (don't reset them)
      this.storedText1 = savedText1 || '';
      this.storedText2 = savedText2 || '';
      
      // Check if overlay is still in the DOM, if not re-attach it
      if (!this.isConnected) {
        console.log('[TextPreviewOverlay] Host overlay was removed, but we are operating in injected mode.');
      }
      
      // Re-initialize visibility based on current image
      if (Shopify?.designMode) {
        this.style.opacity = '1';
        this.style.pointerEvents = 'none';
      }
      
      // Always restore the text preview to all injected instances
      if (this.storedText1) {
        this.updatePreviewText(1, this.storedText1);
      }
      if (this.storedText2) {
        this.updatePreviewText(2, this.storedText2);
      }
      
      console.log('[TextPreviewOverlay] Variant changed, restored text:', { 
        text1: this.storedText1, 
        text2: this.storedText2,
        input1Found: !!this.input1,
        input2Found: !!this.input2
      });
    }, 100);
  }

  /**
   * Re-bind input events after DOM changes
   */
  bindInputEvents() {
    // Remove old listeners first
    if (this.input1) {
      this.input1.removeEventListener('input', this.handleInput);
      this.input1.removeEventListener('change', this.handleInput);
    }
    if (this.input2) {
      this.input2.removeEventListener('input', this.handleInput);
      this.input2.removeEventListener('change', this.handleInput);
    }

    // Find inputs again
    if (this.config.inputSelector1) {
      this.input1 = document.querySelector(this.config.inputSelector1);
    }
    if (this.config.inputSelector2) {
      this.input2 = document.querySelector(this.config.inputSelector2);
    }

    // Re-bind
    if (this.input1) {
      this.input1.addEventListener('input', this.handleInput);
      this.input1.addEventListener('change', this.handleInput);
    }
    if (this.input2) {
      this.input2.addEventListener('input', this.handleInput);
      this.input2.addEventListener('change', this.handleInput);
    }
  }

  /**
   * Handle slideshow slide changes
   * @param {CustomEvent} event 
   */
  handleSlideChange(event) {
    const slideIndex = event.detail?.index ?? 0;
    this.currentSlideIndex = slideIndex;
    
    // Check if we should show on this slide
    if (this.shouldShowOnCurrentImage()) {
      this.style.opacity = '1';
      this.style.pointerEvents = 'none';
      
      // Restore the stored text values
      if (this.storedText1) {
        this.updatePreviewText(1, this.storedText1);
      }
      if (this.storedText2) {
        this.updatePreviewText(2, this.storedText2);
      }
    } else {
      this.style.opacity = '0';
      this.style.pointerEvents = 'none';
    }
  }

  /**
   * Check if the preview should show on the current image
   * Based on alt text filtering and slide position settings
   * @returns {boolean}
   */
  shouldShowOnCurrentImage() {
    // If alt text filtering is enabled, check the current image's alt
    if (this.config.filterByAlt) {
      const currentImageAlt = this.getCurrentImageAlt();
      const shouldShow = currentImageAlt && currentImageAlt.toLowerCase().includes(this.config.altKeyword);
      console.log('[TextPreviewOverlay] shouldShowOnCurrentImage (alt filter):', { 
        currentImageAlt, 
        keyword: this.config.altKeyword, 
        shouldShow 
      });
      return shouldShow;
    }
    
    // If not filtering by alt, use the showOnAllSlides setting
    if (!this.config.showOnAllSlides) {
      // Only show on first slide
      const shouldShow = (this.currentSlideIndex ?? 0) === 0;
      console.log('[TextPreviewOverlay] shouldShowOnCurrentImage (first slide only):', { 
        currentSlideIndex: this.currentSlideIndex, 
        shouldShow 
      });
      return shouldShow;
    }
    
    console.log('[TextPreviewOverlay] shouldShowOnCurrentImage: true (show on all)');
    return true;
  }

  /**
   * Get the alt text of the current image in the slideshow
   * @returns {string|null}
   */
  getCurrentImageAlt() {
    // Try multiple ways to find the section
    let section = this.closest('.shopify-section');
    if (!section && this.sectionElement) {
      section = this.sectionElement;
    }
    if (!section && this.config.sectionId) {
      section = document.querySelector(`[data-section-id="${this.config.sectionId}"]`);
    }
    if (!section) {
      // Last resort: find any section with media gallery
      section = document.querySelector('media-gallery')?.closest('.shopify-section');
    }
    
    if (!section) {
      console.log('[TextPreviewOverlay] getCurrentImageAlt: No section found');
      return null;
    }
    
    const mediaGallery = section.querySelector('media-gallery');
    if (!mediaGallery) {
      // For single image, just get the first image
      const img = section.querySelector('.product-media__image');
      const alt = img?.getAttribute('alt') || null;
      console.log('[TextPreviewOverlay] getCurrentImageAlt (single image):', alt);
      return alt;
    }
    
    // For carousel, find the ACTIVE/VISIBLE slide's image
    // First try to find the active slide
    let currentSlide = mediaGallery.querySelector('slideshow-slide[aria-selected="true"]')
      || mediaGallery.querySelector('slideshow-slide.is-active')
      || mediaGallery.querySelector('slideshow-slide.is-selected');
    
    // If no active slide found, use slide index
    if (!currentSlide) {
      const slideIndex = this.currentSlideIndex ?? 0;
      const slides = mediaGallery.querySelectorAll('slideshow-slide');
      currentSlide = slides[slideIndex];
    }
    
    if (currentSlide) {
      const img = currentSlide.querySelector('.product-media__image, img');
      const alt = img?.getAttribute('alt') || null;
      console.log('[TextPreviewOverlay] getCurrentImageAlt (carousel):', alt);
      return alt;
    }
    
    // Fallback: try to find any visible image
    const visibleImg = mediaGallery.querySelector('.product-media__image');
    const alt = visibleImg?.getAttribute('alt') || null;
    console.log('[TextPreviewOverlay] getCurrentImageAlt (fallback):', alt);
    return alt;
  }

  /**
   * Update the preview text and fit it to the box
   * @param {number} inputNum - 1 or 2
   * @param {string} text - The text to display
   * @param {boolean} isPlaceholder - Whether this is placeholder text (for styling)
   */
  updatePreviewText(inputNum, text, isPlaceholder = false) {
    const showPreview = inputNum === 1 ? this.config.showPreview1 : this.config.showPreview2;
    
    // Find all injected containers
    const containers = document.querySelectorAll(`.text-preview-overlay-container [data-preview-text="${inputNum}"]`);
    
    if (containers.length === 0) return;

    containers.forEach(container => {
      // Update text content
      container.textContent = text;
      
      // Find parent box
      const box = container.closest(`.text-preview-box--${inputNum}`);
      if (!box) return;

      // Show/hide based on content and settings
      if (text && showPreview) {
        box.classList.add('has-text');
        box.style.visibility = 'visible';
        
        // Add/remove placeholder styling
        if (isPlaceholder) {
          box.classList.add('is-placeholder');
        } else {
          box.classList.remove('is-placeholder');
        }
      } else {
        box.classList.remove('has-text');
        box.classList.remove('is-placeholder');
        box.style.visibility = 'hidden';
      }
    });

    // Fit text to box (for all instances)
    if (text) {
      this.fitTextToBox(inputNum);
    }
  }

  /**
   * Auto-fit text to the bounding box by adjusting font size
   * Uses binary search for efficient sizing
   * @param {number} inputNum - 1 or 2
   */
  fitTextToBox(inputNum) {
    const containers = document.querySelectorAll(`.text-preview-overlay-container [data-preview-text="${inputNum}"]`);
    if (containers.length === 0) return;

    // We only need to calculate size once since all boxes are identical
    // Use the first visible one or just the first one for calculation
    const sampleContainer = containers[0];
    const box = sampleContainer.closest(`.text-preview-box--${inputNum}`);
    
    if (!sampleContainer.textContent || !box) return;

    // Temporarily make container visible for measurements if needed
    // Note: We assume the box has dimensions even if hidden, but visibility:hidden elements do have layout
    
    const boxRect = box.getBoundingClientRect();
    const maxWidth = boxRect.width;
    const maxHeight = boxRect.height;

    if (maxWidth === 0 || maxHeight === 0) return;

    // Get the max font size from the box's CSS variable or config
    const computedStyle = getComputedStyle(box);
    const maxFontSize = parseFloat(computedStyle.getPropertyValue('--preview-max-font-size')) || this.config.maxFontSize;
    const minFontSize = parseFloat(computedStyle.getPropertyValue('--preview-min-font-size')) || this.config.minFontSize;

    // Binary search for optimal font size
    let low = minFontSize;
    let high = maxFontSize;
    let optimalSize = minFontSize;

    // Prepare for measurement
    const originalVisibility = sampleContainer.style.visibility;
    sampleContainer.style.visibility = 'visible';
    sampleContainer.style.position = 'absolute';
    sampleContainer.style.whiteSpace = 'nowrap';

    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      sampleContainer.style.fontSize = `${mid}px`;

      const textWidth = sampleContainer.scrollWidth;
      const textHeight = sampleContainer.scrollHeight;

      if (textWidth <= maxWidth && textHeight <= maxHeight) {
        optimalSize = mid;
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }

    // Restore sample style
    sampleContainer.style.fontSize = '';
    sampleContainer.style.visibility = originalVisibility;
    sampleContainer.style.position = '';
    sampleContainer.style.whiteSpace = '';

    // Apply the optimal font size to ALL instances
    containers.forEach(container => {
      container.style.fontSize = `${optimalSize}px`;
      container.dataset.computedFontSize = optimalSize;
    });
  }

  /**
   * Show visual guide in theme editor
   */
  showEditorGuide() {
    this.classList.add('editor-mode');
    
    // Find all injected containers
    const containers = document.querySelectorAll('.text-preview-overlay-container');
    
    // Get placeholder text from config
    const placeholder1 = this.config.placeholder1 || 'Preview Text 1';
    const placeholder2 = this.config.placeholder2 || 'Preview Text 2';
    
    containers.forEach(container => {
      container.classList.add('editor-mode');
      
      // Make bounding boxes visible
      const box1 = container.querySelector('[data-preview-box="1"]');
      const box2 = container.querySelector('[data-preview-box="2"]');
      const text1 = container.querySelector('[data-preview-text="1"]');
      const text2 = container.querySelector('[data-preview-text="2"]');
      
      if (box1) {
        box1.style.visibility = 'visible';
        box1.classList.add('has-text');
        
        // Add placeholder text if empty
        if (text1 && !text1.textContent) {
          text1.textContent = placeholder1;
          box1.classList.add('has-placeholder');
          // Fit placeholder
          this.fitTextToBox(1);
        }
      }
      
      if (box2) {
        box2.style.visibility = 'visible';
        box2.classList.add('has-text');
        
        if (text2 && !text2.textContent) {
          text2.textContent = placeholder2;
          box2.classList.add('has-placeholder');
          this.fitTextToBox(2);
        }
      }
    });
  }

  /**
   * Utility: Debounce function
   * @param {Function} func 
   * @param {number} wait 
   * @returns {Function}
   */
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

// Register the custom element
if (!customElements.get('text-preview-overlay')) {
  customElements.define('text-preview-overlay', TextPreviewOverlay);
}

export default TextPreviewOverlay;
